/* eslint-disable */
// Costs tab + Expenditure add/edit dialog (iter-126k, slice 8b of
// EventDetail.jsx refactor).
//
// Admin-only. The tab lists per-event expenditure rows (category +
// amount + paid/unpaid badge + actions) with a running total at the
// bottom. The dialog opens for both "Add Expenditure" (from the
// header button) and per-row edits.
import React from "react";
import { Edit, FileText, Plus, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";


export const CostsTab = ({
  expenditure,
  expDialog,
  setExpDialog,
  handleDeleteExpenditure,
}) => (
  <Card className="glass-card" data-testid="costs-tab-content">
    <CardHeader className="border-b border-white/5 pb-4">
      <div className="flex items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" /> Event Costs
        </CardTitle>
        <Button
          size="sm"
          onClick={() => setExpDialog({ open: true, data: { paid: true, date: new Date().toISOString().slice(0, 10) } })}
          data-testid="add-expenditure-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Expenditure
        </Button>
      </div>
    </CardHeader>
    <CardContent className="p-6">
      {expenditure.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p>No expenditure logged for this event yet.</p>
          <p className="text-xs mt-1">Venue hire, refreshments, travel costs — all go here.</p>
        </div>
      ) : (
        <>
          <div className="divide-y divide-white/5">
            {expenditure.map((t) => (
              <div key={t.id} className="flex justify-between items-center py-3" data-testid={`exp-row-${t.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{t.category_name}</Badge>
                    <span className="text-sm text-muted-foreground">{t.date}</span>
                    {t.paid ? <Badge variant="secondary" className="text-xs">Paid</Badge> : <Badge variant="outline" className="text-xs">Unpaid</Badge>}
                  </div>
                  <div className="mt-1">{t.description}</div>
                  {t.notes && <div className="text-xs text-muted-foreground mt-1">{t.notes}</div>}
                </div>
                <div className="text-right ml-4">
                  <div className="font-mono font-medium text-rose-400">£{Number(t.amount).toFixed(2)}</div>
                  <div className="flex gap-1 justify-end mt-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setExpDialog({ open: true, data: { ...t } })}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDeleteExpenditure(t.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-white/10 flex justify-between">
            <span className="text-sm text-muted-foreground">Total expenditure</span>
            <span className="font-mono font-semibold text-rose-400" data-testid="costs-total">
              £{expenditure.reduce((s, t) => s + Number(t.amount || 0), 0).toFixed(2)}
            </span>
          </div>
        </>
      )}
    </CardContent>
  </Card>
);


export const ExpenditureDialog = ({
  expDialog,
  setExpDialog,
  financeCategories,
  handleSaveExpenditure,
}) => (
  <Dialog open={expDialog.open} onOpenChange={(o) => !o && setExpDialog({ open: false, data: null })}>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>{expDialog.data?.id ? "Edit" : "Add"} Expenditure</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label>Category</Label>
          <Select
            value={expDialog.data?.category_id || ""}
            onValueChange={(v) => setExpDialog({ open: true, data: { ...expDialog.data, category_id: v } })}
          >
            <SelectTrigger data-testid="exp-category"><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>
              {financeCategories.filter((c) => c.type === "expenditure").map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Amount (£)</Label>
            <Input
              type="number"
              step="0.01"
              value={expDialog.data?.amount ?? ""}
              onChange={(e) => setExpDialog({ open: true, data: { ...expDialog.data, amount: e.target.value } })}
              data-testid="exp-amount"
            />
          </div>
          <div>
            <Label>Date</Label>
            <Input
              type="date"
              value={expDialog.data?.date || ""}
              onChange={(e) => setExpDialog({ open: true, data: { ...expDialog.data, date: e.target.value } })}
            />
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <Input
            value={expDialog.data?.description || ""}
            onChange={(e) => setExpDialog({ open: true, data: { ...expDialog.data, description: e.target.value } })}
            data-testid="exp-description"
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={expDialog.data?.paid ?? true}
            onChange={(e) => setExpDialog({ open: true, data: { ...expDialog.data, paid: e.target.checked } })}
            className="w-4 h-4"
          />
          <span className="text-sm">Paid</span>
        </label>
        <div>
          <Label>Notes</Label>
          <Textarea
            rows={2}
            value={expDialog.data?.notes || ""}
            onChange={(e) => setExpDialog({ open: true, data: { ...expDialog.data, notes: e.target.value } })}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => setExpDialog({ open: false, data: null })}>Cancel</Button>
        <Button onClick={handleSaveExpenditure} data-testid="exp-save">Save</Button>
      </div>
    </DialogContent>
  </Dialog>
);


export default CostsTab;
